import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getTokenPlayload,
  setTermsAndConditionStatus,
  ssoLogin,
  ssoLogout,
  updateTermsAndConditionAcceptance,
} from '../../services/auth.service';
import { useRBACContext } from '@impelsysinc/react-rbac';
import { AdminRoles } from '../../utils/constants/app-constants';
import { CryptoMethods } from '../../utils/crypto-methods';
import { useDispatch } from 'react-redux';
import { updateUser } from '../../store/userSlice';
import TermsAndConditionsModal from '../../components/TermsAndConditionsModal/TermsAndConditionsModal';


const SigninSuccess = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isTermsActionLoading, setIsTermsActionLoading] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [termsAndConditionId, setTermsAndConditionId] = useState<string | number | null>(null);
  const navigate = useNavigate();
  const { setPermissions } = useRBACContext();

  const navigateByRole = (role: string) => {
    if (role === AdminRoles.SuperAdmin) {
      navigate('/organization-listing');
      return;
    }

    navigate('/course-listing');
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('code');
    const handleSsoLogin = async () => {
      try {
        const userData = await ssoLogin(authCode);
        // Sample UI Permission data from backend
        // userData.uiPermissions = { permissions: [{ action: ["get"], resource: "newCourse" }]};
        if (userData?.accessToken && authCode) {
          localStorage.setItem('accessToken', userData.accessToken);
          const user = getTokenPlayload();
          const firstName = await CryptoMethods.ecbDecryptData(user.user_firstName);
          const lastName = await CryptoMethods.ecbDecryptData(user.user_lastName);
          const email = await CryptoMethods.ecbDecryptData(user.user_email);
          dispatch(updateUser({ firstName, lastName, email}));
          setPermissions(userData.uiPermissions.permissions);
          localStorage.setItem('role', userData.role);
          localStorage.setItem(
            "uiPermissions",
            JSON.stringify(userData.uiPermissions.permissions)
          );

          const userId = (user as any)?.user_id;
          const acceptedTermsAndCondition = userData?.termsAndCondition;
          const storedTermsAndConditionId = acceptedTermsAndCondition?.id ?? null;

          if (userId) {
            localStorage.setItem('userId', String(userId));
          }
          if (storedTermsAndConditionId) {
            localStorage.setItem('termsAndConditionId', String(storedTermsAndConditionId));
            setTermsAndConditionId(storedTermsAndConditionId);
          }

          const hasAcceptedTermsAndCondition = Boolean(acceptedTermsAndCondition?.isAccepted);
          setTermsAndConditionStatus(hasAcceptedTermsAndCondition);
          setUserRole(userData.role);
          setLoading(false);

          if (hasAcceptedTermsAndCondition) {
            navigateByRole(userData.role);
            return;
          }

          setShowTermsModal(true);
        } else {
          navigate('/no-permission');
        }

      } catch (error) {
        console.error('SSO Login failed:', error);
        setLoading(false);
        navigate('/no-permission');
      }
    };

    handleSsoLogin();
  }, []);

  const handleTermsAccept = async () => {
    try {
      setIsTermsActionLoading(true);
      const storedTermsAndConditionId = termsAndConditionId ?? localStorage.getItem('termsAndConditionId');

      if (storedTermsAndConditionId) {
        await updateTermsAndConditionAcceptance(storedTermsAndConditionId);
      }

      setTermsAndConditionStatus(true);
      setShowTermsModal(false);
      navigateByRole(userRole);
    } finally {
      setIsTermsActionLoading(false);
    }
  };

  const handleTermsDecline = async () => {
    try {
      setIsTermsActionLoading(true);
      await ssoLogout();
    } finally {
      setIsTermsActionLoading(false);
    }
  };

  return (
    <div className="mt-4">
      {loading && (
        <div className="aui-loader loaderHeight">
          <aui-loader type="content"></aui-loader>
        </div>
      )}
      <TermsAndConditionsModal
        isOpen={showTermsModal}
        onAccept={handleTermsAccept}
        onDecline={handleTermsDecline}
        loading={isTermsActionLoading}
      />
    </div>
  );
};

export default SigninSuccess;
